import { AlertDialog, Box, SelectC, SwitchC, TextFieldC } from 'components'
import {
  PaymentResponsible,
  SelectOrdersCustomers,
  SelectSeller
} from 'modules/orders/components'
import {
  useLoadOrderBusinessUnities,
  useLoadOrderReservations
} from 'modules/orders/hooks'
import { useOrderStore } from 'modules/orders/store'
import { useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { SelectOrderType } from '../../SelectOrderType'
import { SelectCrossSale } from '../../SelectCrossSale'
import { SelectSalesChannels } from '../../SelectSalesChannels'
import { Alert, CircularProgress, Grid } from '@mui/material'
import { CustomerInfoModal } from './customerInfoModal'
import { RecalculeAlert } from '../../recalculeAlert'
import { useDuplicateOrderRecalcStatus } from '../useDuplicateOrderCustomerChange'

export default function General() {
  const { watch, setValue } = useFormContext()
  const { isRecalculatingPrices } = useDuplicateOrderRecalcStatus()

  const setForm = useOrderStore.useSetForm()

  const customer = watch('customer')
  const orderType = watch('orderType')
  const cdPedidoReserva = watch('cdPedidoReserva')

  const [openReserveAlert, setOpenReserveAlert] = useState(false)
  const [hasShownReserveAlert, setHasShownReserveAlert] = useState(false)
  const previousOrderTypeRef = useRef<any>(null)

  const { data: businessUnities, isLoading: businessUnitiesLoading } =
    useLoadOrderBusinessUnities({})

  const { data: customerReservations } = useLoadOrderReservations({
    customerId: customer?.id
  })

  useEffect(() => {
    setForm({ customer })
  }, [customer, setForm])

  useEffect(() => {
    setHasShownReserveAlert(false)
  }, [customer?.id])

  useEffect(() => {
    const hasReservations =
      customerReservations?.data && customerReservations.data.length > 0

    if (customer?.id && hasReservations && !hasShownReserveAlert) {
      setOpenReserveAlert(true)
      setHasShownReserveAlert(true)
    }
  }, [customer?.id, customerReservations?.data, hasShownReserveAlert])

  useEffect(() => {
    // Quando vier apenas o id de cdPedidoReserva (edição),
    // mapeamos para o objeto correspondente da listagem de reservas
    if (!customerReservations?.data || !cdPedidoReserva) return

    if (typeof cdPedidoReserva === 'object') return

    const cdValue = String(cdPedidoReserva).trim()

    const matchedReservation = customerReservations.data.find((order: any) => {
      const ids = [order?.orderId, order?.id, order?.proposalId].filter(Boolean)

      return ids.some((id: any) => String(id).trim() === cdValue)
    })

    if (matchedReservation) {
      setValue('cdPedidoReserva', matchedReservation, { shouldDirty: false })
    }
  }, [cdPedidoReserva, customerReservations?.data, setValue])

  const reservationIds =
    customerReservations?.data
      ?.map((order: any) => order?.orderId || order?.id || order?.proposalId)
      .filter(Boolean)
      .filter(
        (id: any, index: number, self: any[]) => self.indexOf(id) === index
      ) || []

  const formatReservationIds = (ids: string[]) => {
    if (ids.length === 0) return ''
    if (ids.length === 1) return `Nº ${ids[0]}`
    if (ids.length <= 5) return `Nº: ${ids.join(', ')}`
    return `Nº: ${ids.slice(0, 5).join(', ')} e mais ${ids.length - 5}`
  }

  const reserveAlertText = reservationIds.length
    ? `Este cliente já possui ${reservationIds.length} pedido${
        reservationIds.length > 1 ? 's' : ''
      } do tipo reserva.\n${formatReservationIds(reservationIds)}.`
    : 'Este cliente já possui pedidos do tipo reserva.'

  useEffect(() => {
    const previousOrderType = previousOrderTypeRef.current

    // Na primeira montagem, apenas armazenamos o tipo atual e não limpamos o campo.
    if (!previousOrderType && orderType) {
      previousOrderTypeRef.current = orderType
      return
    }

    // Se o tipo de pedido mudou (após a inicialização), limpamos o vínculo de pedido reserva.
    if (previousOrderType?.id !== orderType?.id) {
      setValue('cdPedidoReserva', null)
    }

    previousOrderTypeRef.current = orderType
  }, [orderType, setValue])

  return (
    <Box
      style={{
        width: '100%',
        padding: 12
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <RecalculeAlert />
        </Grid>
        <Grid item xs={12}>
          <SelectC
            name="unity"
            label="Unidade de Negócio"
            options={businessUnities?.data}
            getOptionLabel={(option: any) => option?.id + ' - ' + option?.name}
            loading={businessUnitiesLoading}
          />
        </Grid>
        <Grid item xs={12}>
          <SelectSeller />
        </Grid>
        {/* {user?.role !== userRole.Representative && (
          <Grid item xs={12}>
            <SelectIndicator />
          </Grid>
        )} */}
        {isRecalculatingPrices && (
          <Grid item xs={12}>
            <Alert
              severity="info"
              icon={<CircularProgress style={{ marginTop: 3 }} size={16} />}
            >
              Recalculando itens e totais do pedido...
            </Alert>
          </Grid>
        )}
        <Grid item container spacing={1}>
          <Grid item xs={12}>
            <SelectOrdersCustomers />
          </Grid>
          <Grid item xs={2}>
            <CustomerInfoModal />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <SelectOrderType />
        </Grid>

        <Grid item xs={12}>
          <SelectCrossSale />
        </Grid>

        <Grid item xs={12}>
          <SelectC
            name="cdPedidoReserva"
            label="Vincular Pedido Reserva"
            disabled={
              orderType?.id === 'P' ||
              !customerReservations?.data ||
              customerReservations.data.length === 0
            }
            options={customerReservations?.data || []}
            getOptionLabel={(option: any) =>
              `${
                option?.orderId || option?.id || option?.proposalId || ''
              } - Total: ${option?.totalOrder || 0}`
            }
          />
        </Grid>

        <Grid item xs={12}>
          <TextFieldC name="purchaseOrder" label="Ordem de Compra" />
        </Grid>
        <Grid item xs={12}>
          <TextFieldC
            name="representantOrderNumber"
            label="Número do Pedido do Representante"
          />
        </Grid>
        <Grid item xs={12}>
          <SelectSalesChannels />
        </Grid>
        <Grid item xs={12}>
          <PaymentResponsible loading={false} />
        </Grid>
        <Grid item xs={12}>
          <TextFieldC
            name="observation"
            label="Observação do pedido"
            multiline
            rows={3}
          />
        </Grid>
        <Grid item xs={12}>
          <SwitchC
            name="printObs"
            label="Mostrar observação do pedido no PDF"
          />
        </Grid>
      </Grid>
      {openReserveAlert && (
        <AlertDialog
          open={openReserveAlert}
          title="Pedidos de reserva encontrados"
          text={reserveAlertText}
          onClose={() => setOpenReserveAlert(false)}
          okButtonLabel="Fechar"
          hideCancelButton
        />
      )}
    </Box>
  )
}
